// src/api/contracts.ts

import { apiCweb } from './api-cweb';
import { appLog } from '~src/utils/log';

// =================================== CONTRACTS
// Get all contracts
export const getContracts = async ({
  projectId,
  tenderId,
  show_deleted,
}: {
  projectId: number | string;
  tenderId: number | string;
  show_deleted?: boolean;
}) => {
  const url = `/projects/${projectId}/tenders/${tenderId}/contracts`;
  appLog('getContracts ->', url, { show_deleted });

  const res = await apiCweb.get(url, {
    params: {
      ...(show_deleted ? { show_deleted: true } : {}),
    },
  });

  appLog('getContracts #2 ->', res.data);
  return res.data;
};

interface createContractParams {
  projectId: number;
  tenderId: number;
  data: {
    name: string;
    description?: string;
    reference?: string;
  };
}

// Create a new contract
export const createContract = async ({
  projectId,
  tenderId,
  data,
}: createContractParams) => {
  appLog('createContract ->', { projectId, tenderId, data });

  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts`,
    data
  );

  return res.data;
};

interface editContractParams {
  projectId: number;
  tenderId: number;
  contractId: number | undefined;
  data: {
    name: string;
    description?: string;
    reference?: string;
  };
}

// Edit contract
export const editContract = async ({
  projectId,
  tenderId,
  contractId,
  data,
}: editContractParams) => {
  appLog('editContract ->', { projectId, tenderId, contractId, data });
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}`,
    data
  );
};

interface deleteContractParams {
  projectId: number;
  tenderId: number;
  contractId: number;
}

// Delete contract
export const deleteContract = async ({
  projectId,
  tenderId,
  contractId,
}: deleteContractParams) => {
  appLog('deleteContract ->', { projectId, tenderId, contractId });
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}`
  );
  return res.data;
};

// ======================================== VOUCHERS
// Get Vouchers
export const getVouchers = async ({
  projectId,
  tenderId,
  contractId,
  show_deleted,
}: {
  projectId: number;
  tenderId: number;
  contractId: number | null;
  show_deleted?: boolean;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers`,
    {
      params: {
        ...(show_deleted ? { show_deleted: true } : {}),
      },
    }
  );
  return res.data;
};

// Add Vouchers
export interface CreateVoucherParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  data: {
    name: string;
    description?: string;
    voucher_type: number;
    unit_count?: number;
    unit_type?: number;
    sort?: number;
    voucher_group_id?: number;
    is_optional?: boolean;
  };
}

export const createVoucher = async ({
  projectId,
  tenderId,
  contractId,
  data,
}: CreateVoucherParams) => {
  const response = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers`,
    data
  );

  appLog(`createVoucher LOG ->`, response.data);
  return response.data;
};

// Delete Vouchers
export interface DeleteVoucherParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
}

export const deleteVoucher = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
}: DeleteVoucherParams): Promise<void> => {
  appLog(`deleteVoucher LOG ->`, {
    projectId,
    tenderId,
    contractId,
    voucherId,
  });
  await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}`
  );
};

// Update a voucher
export interface UpdateVoucherParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
  data: {
    name: string;
    description?: string;
    unit_count?: number;
    unit_type?: number;
    sort?: number;
    is_optional?: boolean;
  };
}

export const updateVoucher = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
  data,
}: UpdateVoucherParams) => {
  appLog(`ToEdit updateVoucher LOG ->`, { data });
  await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}?action=update`,
    data
  );
};

// ======================================== GROUPS
// Add Voucher Group
export interface createVoucherGroup {
  projectId: number;
  tenderId: number;
  contractId: number;
  data: {
    name: string;
    description?: string;
    sort?: number;
    is_ask_price?: boolean;
  };
}

export const createVoucherGroup = async ({
  projectId,
  tenderId,
  contractId,
  data,
}: createVoucherGroup): Promise<void> => {
  appLog(`createVoucherGroup ->`, projectId, tenderId, contractId, data);

  const response = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/voucher_groups`,
    data
  );

  appLog(`createVoucherGroup LOG ->`, response.data);
  return response.data;
};

// Delete Group Vouchers
export interface DeleteVoucherGroupParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
}

export const deleteVoucherGroup = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
}: DeleteVoucherGroupParams): Promise<void> => {
  appLog(`deleteVoucherGroup LOG ->`, {
    projectId,
    tenderId,
    contractId,
    voucherId,
  });
  await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/voucher_groups/${voucherId}`
  );
};

// Update Voucher Group
export interface updateVoucherGroupParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
  data: {
    name: string;
    description?: string;
    sort?: number;
    is_ask_price?: boolean;
  };
}

export const updateVoucherGroup = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
  data,
}: updateVoucherGroupParams): Promise<void> => {
  await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/voucher_groups/${voucherId}`,
    data
  );
};

// =========================================================================

// Reorder all vouchers (cards + groups)
export interface UpdateVoucherOrderParams {
  projectId: number;
  tenderId: number;
  contractId: number;
  order: { voucher_id: number; order_flag: number }[];
}

export const updateVoucherOrder = async ({
  projectId,
  tenderId,
  contractId,
  order,
}: UpdateVoucherOrderParams): Promise<void> => {
  await apiCweb.patch(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/order`,
    order
  );
};

// Move card to group or root
export const assignVoucherToGroup = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
  voucher_group_id,
  sort,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
  voucher_group_id?: number;
  sort: number;
}) => {
  const query = new URLSearchParams({
    action: 'assign_to_voucher_group',
    ...(voucher_group_id !== undefined && {
      voucher_group_id: String(voucher_group_id),
    }),
    sort: String(sort),
  }).toString();

  return apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}?${query}`,
    {}
  );
};

// ======================== VOUCHERS
// Get all attachment files from selected voucher card
export const getVoucherAttachments = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
}: {
  projectId: number | string;
  tenderId: number | string;
  contractId: number | string;
  voucherId: number | string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}/attachments`,
    { params: { show_all_files: true } }
  );
  return res.data;
};

// Delete Attachment from voucher card
export const deleteVoucherAttachment = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
  fileGuid,
}: {
  projectId: number | string;
  tenderId: number | string;
  contractId: number | string;
  voucherId: number | string;
  fileGuid: string;
}) => {
  appLog(`Delete attachment LOG ->`, {
    projectId,
    tenderId,
    contractId,
    voucherId,
    fileGuid,
  });
  return apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}/attachments/${fileGuid}`
  );
};

// Get link for downloading attrachment file from voucher card
export const getVoucherAttachmentDownloadUrl = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
  fileGuid,
}: {
  projectId: number | string;
  tenderId: number | string;
  contractId: number | string;
  voucherId: number | string;
  fileGuid: string;
}) => {
  const res = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}/attachments/${fileGuid}/location`
  );
  return res.headers.location;
};

// Get link for downloading attrachment file from OFFER's voucher card for Project user
export const getOffersVoucherAttachmentDownloadUrl = async ({
  projectId,
  tenderId,
  offerId,
  voucherId,
  fileGuid,
}: {
  projectId: number | string;
  tenderId: number | string;
  offerId: number | string;
  voucherId: number | string;
  fileGuid: string;
}) => {
  const res = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/voucher_responses/${voucherId}/attachment/${fileGuid}/location`
  );
  return res.headers.location;
};

// ====== USER CONTRACTS ===========

type sharedTenderContractsParams = {
  share_id: string;
  Share_Key: string;
};

// Get Contracts from user-side
export const shareTenderContracts = async ({
  share_id,
  Share_Key,
}: sharedTenderContractsParams) => {
  appLog('getUserContracts ->', { share_id, Share_Key });
  const res = await apiCweb.get(`/tender_shares/${share_id}/contracts`, {
    headers: { 'Share-Key': Share_Key },
  });

  return res.data;
};

type sharedTenderContractsVouchersParams = {
  share_id: string;
  Share_Key: string;
  contracts_id: number;
};

// Get Contract's vouchers from user-side
export const shareTenderContractsVouchers = async ({
  share_id,
  Share_Key,
  contracts_id,
}: sharedTenderContractsVouchersParams) => {
  appLog('getUserContractsVouchers ->', { share_id, Share_Key, contracts_id });
  const res = await apiCweb.get(
    `/tender_shares/${share_id}/contracts/${contracts_id}/vouchers`,
    {
      headers: { 'Share-Key': Share_Key },
    }
  );

  return res.data;
};

// ====== Tender Registration Core (Details Tab)

// project side
export const getContractsAndVouchersProjectSide = async ({
  projectId,
  tenderId,
}: {
  projectId: number;
  tenderId: number;
}) => {
  appLog('getContractsAndVouchersProjectSide ->', { projectId, tenderId });
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/core_contracts`
  );

  appLog(`getContractsAndVouchersProjectSide -> res`, res.data);
  return res.data;
};

// vendor side
export const getContractsAndVouchersMyTenderSide = async ({
  share_id,
  Share_Key,
}: {
  share_id: string;
  Share_Key: string;
}) => {
  appLog('getContractsAndVouchersMyTenderSide ->', { share_id, Share_Key });
  const res = await apiCweb.get(`/tender_shares/${share_id}/core_contracts`, {
    headers: { 'Share-Key': Share_Key },
  });

  appLog('getContractsAndVouchersMyTenderSide -> res', res.data);
  return res.data;
};

/**
 * Set or update the winner offer for a contract
 * PUT /v1/projects/{project_id}/tenders/{tender_id}/contracts/{contract_id}/winner
 */
export const setContractWinner = async ({
  projectId,
  tenderId,
  contractId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  body: {
    offer_id: number;
    note?: string;
  };
}) => {
  appLog('setContractWinner ->', { projectId, tenderId, contractId, body });
  const res = await apiCweb.put<void>(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/winner`,
    body
  );
  return res.data;
};

/**
 * Remove the winner offer from a contract
 * DELETE /v1/projects/{project_id}/tenders/{tender_id}/contracts/{contract_id}/winner
 */
export const removeContractWinner = async ({
  projectId,
  tenderId,
  contractId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
}) => {
  appLog('removeContractWinner ->', { projectId, tenderId, contractId });
  const res = await apiCweb.delete<void>(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/winner`
  );
  return res.data;
};

export const reactivateContract = async ({
  projectId,
  tenderId,
  contractId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
}) => {
  appLog('reactivateContract ->', { projectId, tenderId, contractId });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}?action=reactivate`,
    {}
  );

  return res.data;
};

export const reactivateVoucher = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
}) => {
  return apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}?action=reactivate`,
    {}
  );
};

export const reactivateVoucherGroup = async ({
  projectId,
  tenderId,
  contractId,
  voucherId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  voucherId: number;
}) => {
  return apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/voucher_groups/${voucherId}?action=reactivate`,
    {}
  );
};
